import { getChartTitle } from '@/shared/copy/charts';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { formatPercent } from '@/ui/formatters/formatPercent';
import type { MetricsDto } from '@/features/metrics/metricsHelpers';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { StatusTone } from '@/ui/primitives/Chip';
import type { MetricThresholdGroup } from '@flowqa/shared';

export type MetricDeltaTone = 'positive' | 'negative' | 'neutral';

export type MetricValueItem = {
  id: string;
  label: string;
  valueLabel: string;
  valueTone: StatusTone;
  deltaValue: number | null;
  deltaLabel: string | null;
  deltaTone: MetricDeltaTone;
};

const resolveDeltaTone = (
  delta: number | null,
  direction: MetricThresholdGroup[keyof MetricThresholdGroup]['direction'],
): MetricDeltaTone => {
  if (delta === null || delta === 0) return 'neutral';
  if (direction === 'higherBetter') return delta > 0 ? 'positive' : 'negative';
  return delta < 0 ? 'positive' : 'negative';
};

const formatDelta = (
  value: number | null,
  formatter: (value: number) => string,
  showPlaceholder: boolean,
): string => {
  if (value === null || value === 0) return showPlaceholder ? '-' : '';
  const sign = value > 0 ? '+' : '-';
  return `${sign}${formatter(Math.abs(value))}`;
};

const resolveValue = (value: number | null, formatter: (value: number) => string): string =>
  value === null ? '-' : formatter(value);

const resolvePassRate = (metrics: MetricsDto | null): number | null => {
  if (!metrics) return null;
  const passed = metrics.statusMetrics.find((metric) => metric.key === 'passed');
  return passed?.percentage ?? null;
};

export const buildMetricValueItems = ({
  metrics,
  previousMetrics,
  thresholds,
  frequencyOverride,
  previousFrequencyOverride,
  avgDurationOverride,
  previousAvgDurationOverride,
  passRateOverride,
  previousPassRateOverride,
  flakeScoreOverride,
  previousFlakeScoreOverride,
  showDelta,
  visibleMetricKeys,
}: {
  metrics: MetricsDto | null;
  previousMetrics: MetricsDto | null;
  thresholds: MetricThresholdGroup;
  frequencyOverride?: number | null;
  previousFrequencyOverride?: number | null;
  avgDurationOverride?: number | null;
  previousAvgDurationOverride?: number | null;
  passRateOverride?: number | null;
  previousPassRateOverride?: number | null;
  flakeScoreOverride?: number | null;
  previousFlakeScoreOverride?: number | null;
  showDelta?: boolean;
  visibleMetricKeys?: MetricThresholdGroupKey[];
}): MetricValueItem[] => {
  const includeDeltas = showDelta ?? Boolean(previousMetrics);
  const passRateValue = passRateOverride ?? resolvePassRate(metrics);
  const passRateDelta =
    passRateValue !== null
      ? passRateValue -
        (previousPassRateOverride ?? resolvePassRate(previousMetrics) ?? passRateValue)
      : null;
  const passRateLabel = resolveValue(passRateValue, (value) => `${formatPercent(value)}%`);
  const passRateDeltaLabel = includeDeltas
    ? formatDelta(passRateDelta, (value) => `${formatPercent(value)}%`, includeDeltas)
    : '';
  const passRateTone = getThresholdChipKey(passRateValue, thresholds.passRate) ?? 'grey';

  const frequencyValue = frequencyOverride ?? metrics?.averages.frequencyPerDay ?? null;
  const frequencyDelta =
    frequencyValue !== null
      ? frequencyValue -
        (previousFrequencyOverride ?? previousMetrics?.averages.frequencyPerDay ?? frequencyValue)
      : null;
  const frequencyLabel = resolveValue(frequencyValue, formatFrequency);
  const frequencyDeltaLabel = includeDeltas
    ? formatDelta(frequencyDelta, formatFrequency, includeDeltas)
    : '';
  const frequencyTone = getThresholdChipKey(frequencyValue, thresholds.frequency) ?? 'grey';

  const flakeScoreValue = flakeScoreOverride ?? metrics?.averages.flakeScore ?? null;
  const flakeScoreDelta =
    flakeScoreValue !== null
      ? flakeScoreValue -
        (previousFlakeScoreOverride ?? previousMetrics?.averages.flakeScore ?? flakeScoreValue)
      : null;
  const flakeScoreLabel = resolveValue(flakeScoreValue, (value) => `${formatPercent(value)}%`);
  const flakeScoreDeltaLabel = includeDeltas
    ? formatDelta(flakeScoreDelta, (value) => `${formatPercent(value)}%`, includeDeltas)
    : '';
  const flakeScoreTone = getThresholdChipKey(flakeScoreValue, thresholds.flakeScore) ?? 'grey';

  const durationSeconds = avgDurationOverride ?? metrics?.averages.avgDurationSeconds ?? null;
  const durationDelta =
    durationSeconds !== null
      ? durationSeconds -
        (previousAvgDurationOverride ??
          previousMetrics?.averages.avgDurationSeconds ??
          durationSeconds)
      : null;
  const durationLabel = resolveValue(durationSeconds, (value) => formatDuration(value * 1000));
  const durationDeltaLabel = includeDeltas
    ? formatDelta(
        durationDelta,
        (value) => formatDuration(value * 1000, { roundMillisecondsWithSeconds: true }),
        includeDeltas,
      )
    : '';
  const durationTone = getThresholdChipKey(durationSeconds, thresholds.avgDuration) ?? 'grey';

  const items: MetricValueItem[] = [
    {
      id: 'passRate',
      label: getChartTitle('passRate'),
      valueLabel: passRateLabel,
      valueTone: passRateTone,
      deltaValue: passRateDelta,
      deltaLabel: passRateDeltaLabel,
      deltaTone: resolveDeltaTone(passRateDelta, thresholds.passRate.direction),
    },
    {
      id: 'frequency',
      label: getChartTitle('frequency'),
      valueLabel: frequencyLabel,
      valueTone: frequencyTone,
      deltaValue: frequencyDelta,
      deltaLabel: frequencyDeltaLabel,
      deltaTone: resolveDeltaTone(frequencyDelta, thresholds.frequency.direction),
    },
    {
      id: 'avgDuration',
      label: getChartTitle('avgDuration'),
      valueLabel: durationLabel,
      valueTone: durationTone,
      deltaValue: durationDelta,
      deltaLabel: durationDeltaLabel,
      deltaTone: resolveDeltaTone(durationDelta, thresholds.avgDuration.direction),
    },
    {
      id: 'flakeScore',
      label: getChartTitle('flakeScore'),
      valueLabel: flakeScoreLabel,
      valueTone: flakeScoreTone,
      deltaValue: flakeScoreDelta,
      deltaLabel: flakeScoreDeltaLabel,
      deltaTone: resolveDeltaTone(flakeScoreDelta, thresholds.flakeScore.direction),
    },
  ];
  if (!visibleMetricKeys) return items;
  const visibleMetricKeySet = new Set<string>(visibleMetricKeys);
  return items.filter((item) => visibleMetricKeySet.has(item.id));
};
